// The frame for world-scoped screens that still wear the city header (Reports, Mail, Guild, Leaderboards, Profile,
// Titles, Quests): the current city's village/get (polled), the city switcher — which here only changes the current
// city and stays on the screen — and the menu badges. `useWorldCity()` gives the screen the same view of the city.
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import type { Village } from "@/api/types";
import { Page, type HeaderSpec, type PageLayout } from "@/app/Page";
import { Sheet } from "@/kit/overlays";
import { CitySwipe } from "@/kit/CitySwipe";
import { CityPickList } from "@/kit/CityPick";
import { useCityActions } from "@/state/actions";
import { ownVillages, useCurrentCity } from "@/state/queries";
import { useSession } from "@/state/session";
import { fmtCoords, fmtNum, plural } from "@/game/format";
import { claimableQuests } from "@/game/city";

export interface WorldCtx {
  wid: number;
  /** the current city (null until the worlds overview has loaded for a first visit) */
  vid: number | null;
  v: Village | undefined;
  loading: boolean;
  error: boolean;
  actions: ReturnType<typeof useCityActions>;
  /** /w/2 */
  base: string;
  openSwitcher: () => void;
}

const Ctx = createContext<WorldCtx | null>(null);

export const useWorldCity = (): WorldCtx => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useWorldCity outside a WorldPage");
  return c;
};

/** The header's Reports / Mail dots from a city's new_report / new_mail flags. */
export const menuBadgesOf = (v: Village | undefined) => (v ? { reports: v.new_report ? 1 : undefined, mail: v.new_mail ? 1 : undefined } : undefined);

/** The bottom nav's counts from a city: claimable quests on Quests, incoming attacks on the Map (Empire's old badge). */
export const navBadgesOf = (v: Village | undefined) => (v ? { quests: claimableQuests(v) || undefined, map: v.num_incoming_attacks || undefined } : undefined);

/** The city box's second line: "(505, 518) · 563 pts · 2 cities" (the live game's coords and points). */
export const cityMeta = (v: Village | undefined, cities: number): ReactNode =>
  v ? (<>{fmtCoords(v.village?.coords)} · <b>{fmtNum(v.village?.points)}</b> pts · {plural(cities || 1, "city", "cities")}</>) : "";

export function WorldPage({ children, header, nav = true, citySwipe = false, layout }: { children: ReactNode; header?: Partial<HeaderSpec>; nav?: boolean; layout?: PageLayout; /** phones: swipe the header / nav to change city (the live game does this on the Guild) */ citySwipe?: boolean }) {
  const { wid: widP } = useParams();
  const wid = Number(widP);
  const session = useSession();
  const { vid, v, q, setVillage } = useCurrentCity(wid);
  const actions = useCityActions(wid, vid ?? 0);
  const [switcher, setSwitcher] = useState(false);
  const cities = ownVillages(v, session.uid);

  const ctx = useMemo<WorldCtx>(
    () => ({ wid, vid, v, loading: q.isLoading || (!vid && !q.isError), error: q.isError, actions, base: `/w/${wid}`, openSwitcher: () => setSwitcher(true) }),
    [wid, vid, v, q.isLoading, q.isError, actions],
  );

  const spec = {
    kind: "city",
    city: v?.village?.city_name ?? (ctx.loading ? "…" : "City"),
    meta: cityMeta(v, cities.length),
    onCity: () => setSwitcher(true),
    v,
    ...(header as object),
  } as HeaderSpec;

  return (
    <Ctx.Provider value={ctx}>
      <Page
        header={spec}
        nav={nav}
        layout={layout}
        badges={navBadgesOf(v)}
        menuBadges={menuBadgesOf(v)}
        notifications={v?.new_notifications ? 1 : undefined}
        avatarLevel={v?.player_stats?.rank ? fmtNum(v.player_stats.rank) : undefined}
      >
        {children}
        {citySwipe && <CitySwipe wid={wid} vid={vid} cities={cities} onSwitch={setVillage} />}
        <Sheet open={switcher} onClose={() => setSwitcher(false)} from="top" anchor=".whdr-bar" label="Choose a city" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>Your cities · World {wid}</h3>}>
          <CityPickList wid={wid} cities={cities} current={vid} onPick={(c) => { setSwitcher(false); if (c.id !== vid) setVillage(c.id); }} />
        </Sheet>
      </Page>
    </Ctx.Provider>
  );
}

